import { create } from "zustand";
import dayjs from "dayjs";

export type PeriodPreset = "1" | "3" | "6" | "12";

interface AppliedParams {
  searchKeyword?: string;
  startDate: string;
  endDate: string;
}

interface UsageHistoryState {
  // 검색 입력 상태
  searchKeyword: string;
  startDate: string;
  endDate: string;
  periodPreset: PeriodPreset;
  isCustomDateRange: boolean;
  expanded: boolean;
  // 실제 조회에 사용된 파라미터 (조회 버튼 클릭 시 갱신)
  appliedParams: AppliedParams;
  // 조회 트리거 (같은 조건이어도 재호출하기 위한 키)
  refetchKey: number;
  // 마지막 액세스 시각 (오래된 상태는 자동 리셋)
  lastTouchedAt: number;

  setSearchKeyword: (v: string) => void;
  setStartDate: (v: string) => void;
  setEndDate: (v: string) => void;
  setPeriodPreset: (v: PeriodPreset) => void;
  setIsCustomDateRange: (v: boolean) => void;
  setExpanded: (v: boolean) => void;
  applySearch: (params: AppliedParams) => void;
  reset: () => void;
  /** "초기화" 버튼용: 입력 필드만 디폴트로 되돌리고 펼침 상태/결과는 유지 */
  resetInputs: () => void;
  /** 일정 시간(ms) 이상 방치된 상태면 디폴트로 리셋. true 반환 시 리셋 발생 */
  resetIfStale: (maxIdleMs: number) => boolean;
}

const buildDefaults = () => {
  const t = dayjs();
  const start = t.subtract(1, "month").format("YYYY-MM-DD");
  const end = t.format("YYYY-MM-DD");
  return {
    searchKeyword: "",
    startDate: start,
    endDate: end,
    periodPreset: "1" as PeriodPreset,
    isCustomDateRange: false,
    expanded: false,
    appliedParams: { startDate: start, endDate: end } satisfies AppliedParams,
    refetchKey: 0,
  };
};

export const useUsageHistoryStore = create<UsageHistoryState>((set, get) => ({
  ...buildDefaults(),
  lastTouchedAt: Date.now(),

  setSearchKeyword: (v) => set({ searchKeyword: v, lastTouchedAt: Date.now() }),
  setStartDate: (v) => set({ startDate: v, lastTouchedAt: Date.now() }),
  setEndDate: (v) => set({ endDate: v, lastTouchedAt: Date.now() }),
  setPeriodPreset: (v) => set({ periodPreset: v, lastTouchedAt: Date.now() }),
  setIsCustomDateRange: (v) =>
    set({ isCustomDateRange: v, lastTouchedAt: Date.now() }),
  setExpanded: (v) => set({ expanded: v, lastTouchedAt: Date.now() }),

  applySearch: (params) =>
    set((s) => ({
      appliedParams: params,
      refetchKey: s.refetchKey + 1,
      lastTouchedAt: Date.now(),
    })),

  reset: () => set({ ...buildDefaults(), lastTouchedAt: Date.now() }),

  resetInputs: () => {
    const d = buildDefaults();
    set({
      searchKeyword: d.searchKeyword,
      startDate: d.startDate,
      endDate: d.endDate,
      periodPreset: d.periodPreset,
      isCustomDateRange: d.isCustomDateRange,
      lastTouchedAt: Date.now(),
    });
  },

  resetIfStale: (maxIdleMs) => {
    const { lastTouchedAt } = get();
    if (Date.now() - lastTouchedAt > maxIdleMs) {
      set({ ...buildDefaults(), lastTouchedAt: Date.now() });
      return true;
    }
    return false;
  },
}));
